import React, { useEffect, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Box, Container, Typography, useMediaQuery } from '@material-ui/core'
import { AnimatePresence, motion } from 'framer-motion'
// core components
import CyberpunkHeader from 'components/Header/CyberpunkHeader.js'
import CyberpunkHeaderLinks from 'components/CyberpunkHeaderLinks.js'
import HeaderNavLinks from 'components/HeaderNavLinks.js'
import CyberpunkResumeScrolling, { DownloadResumeButton } from 'components/CyberpunkResume/CyberpunkResumeScrolling.js'
import CyberpunkHRFAQ from 'components/CyberpunkHRFAQ/CyberpunkHRFAQ.js'
import HUDOverlay from 'components/HUDOverlay.js'
import ContentModal from 'components/ContentModal.js'
import cyberpunkStyles from 'components/3D/CyberpunkStyles.js'

import styles from 'assets/jss/material-kit-react/views/cyberLandingPage.js'

const useStyles = makeStyles(styles)

function CyberLandingPage (props) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [showResume, setShowResume] = useState(false)
  const [showHRFAQ, setShowHRFAQ] = useState(false)
  const [selectedDestination, setSelectedDestination] = useState('resume') // 'resume' or 'hrfaq'
  const [inHUDMode, setInHUDMode] = useState(false) // Whether user is in HUD perspective
  const [showHUD, setShowHUD] = useState(false)
  const [keySequence, setKeySequence] = useState([])
  const [easterEggActivated, setEasterEggActivated] = useState(false)
  const { ...rest } = props

  // Define action handlers
  const handleResumeAction = () => {
    setShowHRFAQ(false)
    setShowResume(true)
  }

  const handleHRFAQAction = () => {
    setShowResume(false)
    setShowHRFAQ(true)
  }

  const handleGoBack = () => {
    setShowResume(false)
    setShowHRFAQ(false)
    setInHUDMode(true)
  }

  const handleActivateResume = () => {
    console.log('Activating Resume from HUD')
    setInHUDMode(false)
    handleResumeAction()
  }

  const handleActivateHRFAQ = () => {
    console.log('Activating HR FAQ from HUD')
    setInHUDMode(false)
    handleHRFAQAction()
  }

  // Keyboard navigation for destination selection
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showResume || showHRFAQ) return // Don't navigate if modal is open

      if (e.key === 'ArrowLeft') {
        console.log('Arrow Left - Selecting Resume')
        setSelectedDestination('resume')
      } else if (e.key === 'ArrowRight') {
        console.log('Arrow Right - Selecting HR FAQ')
        setSelectedDestination('hrfaq')
      } else if (e.key === 'Enter') {
        console.log('Enter pressed - Activating:', selectedDestination)
        if (inHUDMode) {
          // Activate selected destination from HUD
          setInHUDMode(false)
          if (selectedDestination === 'resume') {
            handleResumeAction()
          } else {
            handleHRFAQAction()
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedDestination, showResume, showHRFAQ, inHUDMode])

  // Debug: Log when selectedDestination changes
  useEffect(() => {
    console.log('Selected Destination Changed:', selectedDestination)
  }, [selectedDestination])

  // Keyboard shortcuts and easter eggs
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Easter egg: Konami code (up, up, down, down, left, right, left, right, b, a)
      const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA']

      setKeySequence(prev => {
        const newSequence = [...prev, event.code].slice(-10)
        if (newSequence.join(',') === konamiCode.join(',')) {
          setEasterEggActivated(true)
          console.log('🎮 KONAMI CODE ACTIVATED! Welcome to the Matrix, Neo...')
          setTimeout(() => setEasterEggActivated(false), 5000)
        }
        return newSequence
      })

      // Keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
        case '1':
          event.preventDefault()
          handleResumeAction()
          break
        case '2':
          event.preventDefault()
          handleHRFAQAction()
          break
        case 'h':
          event.preventDefault()
          console.log('🔧 KEYBOARD SHORTCUTS:\n• Ctrl/Cmd + 1: Execute resume.exe\n• Ctrl/Cmd + 2: Execute hrfaq.exe\n• Ctrl/Cmd + H: Show this help')
          break
        }
      }

      // Hidden developer commands
      if (event.key === 'F12' || (event.ctrlKey && event.shiftKey && event.key === 'I')) {
        console.log('🛠️ DEVELOPER MODE: Inspect Element activated! Welcome, fellow developer 👨‍💻')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleResumeAction, handleHRFAQAction])

  return (
    <Box style={cyberpunkStyles.cyberpunkContainer}>
      <style>{cyberpunkStyles.cyberpunkLetterFlicker}</style>
      <Box className={classes.root}>
        {/* Header - hide when HUD mode or viewing content */}
        {!inHUDMode && !showResume && !showHRFAQ && (
          <CyberpunkHeader
            color="transparent"
            routes={[]}
            brand={<>[HOME]</>}
            rightLinks={<CyberpunkHeaderLinks/>}
            leftLinks={<HeaderNavLinks/>}
            fixed
            changeColorOnScroll={{
              height: 100,
              color: 'dark'
            }}
            {...rest}
          />
        )}

        {/* Navigation Breadcrumbs */}
        {(showResume || showHRFAQ) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: 'fixed',
              top: '100px',
              left: '20px',
              zIndex: 999,
              background: 'rgba(0, 0, 0, 0.8)',
              border: '1px solid rgba(0, 255, 255, 0.4)',
              borderRadius: '20px',
              padding: '0.5rem 1rem',
              backdropFilter: 'blur(10px)',
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: '0.8rem',
              color: '#00ffff',
              marginTop: '1rem'
            }}
          >
            {/*Too Long */}
            {/*<span style={{ color: '#888' }}>C:\STANLEY_CHAN\bin\</span>*/}
            {showResume && <span style={{ color: '#ff00ff' }}>resume.exe</span>}
            {showHRFAQ && <span style={{ color: '#ff00ff' }}>hrfaq.exe</span>}
            <span style={{ color: '#00ff00', marginLeft: '0.5rem' }}>● RUNNING</span>
          </motion.div>
        )}

        {/* Hero Section - Portfolio Focus */}
        <Box style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          background: '#080808',
          borderBottom: '1px solid rgba(0, 255, 255, 0.25)',
          paddingBottom: '2rem'
        }}>
          <Container maxWidth="lg" style={{
            textAlign: 'center',
            zIndex: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingTop: '10rem'
          }}>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                padding: '0 1rem',
                gap: '0.5rem'
              }}
            >
              {/* First Name */}
              <Typography variant="h1" style={{
                fontFamily: '"Orbitron", sans-serif',
                fontSize: isMobile ? 'clamp(2rem, 13vw, 4.5rem)' : 'clamp(2.5rem, 8vw, 4.5rem)',
                fontWeight: 700,
                color: '#00ffff',
                textAlign: 'center',
                textShadow: '0 0 20px rgba(0, 255, 255, 0.6)',
                letterSpacing: isMobile ? '0.05em' : '0.02em',
                whiteSpace: 'nowrap',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {isMobile ? 'Stanley' : (
                  'Stanley'.split('').map((char, index) => (
                    <span
                      key={index}
                      className="neon-letter"
                      data-char={char}
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        animation: `neonFlickerOnce 4s forwards`,
                        animationDelay: `${index * 0.15}s`,
                      }}
                    >
                      {char}
                    </span>
                  ))
                )}
              </Typography>

              {/* Last Name */}
              <Typography variant="h1" style={{
                fontFamily: '"Orbitron", sans-serif',
                fontSize: isMobile ? 'clamp(2rem, 13vw, 4.5rem)' : 'clamp(2.5rem, 8vw, 4.5rem)',
                fontWeight: 700,
                color: '#00ffff',
                marginBottom: '1.5rem',
                textAlign: 'center',
                textShadow: '0 0 20px rgba(0, 255, 255, 0.6)',
                letterSpacing: isMobile ? '0.05em' : '0.02em',
                whiteSpace: 'nowrap',
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {isMobile ? 'Chan' : (
                  'Chan'.split('').map((char, index) => (
                    <span
                      key={index}
                      className="neon-letter"
                      data-char={char}
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        animation: `neonFlickerOnce 4s forwards`,
                        animationDelay: `${(7 + index) * 0.15}s`,
                      }}
                    >
                      {char}
                    </span>
                  ))
                )}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 1 }}
            >
              <Typography variant="h5" style={{
                fontFamily: '"Share Tech Mono", monospace',
                color: '#ff00ff',
                fontSize: '1.3rem',
                textShadow: '0 0 10px rgba(255, 0, 255, 0.3)',
                marginBottom: '2rem'
              }}>
                Full Stack Software Engineer (Backend Heavy)
              </Typography>

              {/* Status Chips */}
              <Box style={{ display: 'flex', gap: '1rem', justifyContent: 'center', alignItems: 'center' }}>
                <Box style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(45deg, #00ff00 30%, #00cc00 90%)',
                  color: '#000',
                  borderRadius: '20px',
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(0, 255, 0, 0.3)'
                }}>
                  ● AVAILABLE
                </Box>
                <Box style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(45deg, #0077b5 30%, #00a0dc 90%)',
                  color: '#fff',
                  borderRadius: '20px',
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(0, 119, 181, 0.3)'
                }}>
                  US CITIZEN
                </Box>
              </Box>
            </motion.div>
          </Container>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 1,
              delay: 1.8,
              repeat: Infinity,
              repeatType: 'reverse',
              repeatDelay: 0.5
            }}
            style={{
              textAlign: 'center',
              paddingBottom: '2rem',
              paddingTop: '3rem',
              zIndex: 2
            }}
          >
            <Typography style={{
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: '0.8rem',
              color: '#00ffff',
              marginBottom: '0.5rem',
              textShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
            }}>
              [SCROLL_FOR_MORE]
            </Typography>
            <Box style={{
              width: '2px',
              height: '30px',
              background: 'linear-gradient(180deg, transparent, #00ffff, transparent)',
              margin: '0 auto',
              animation: 'pulse 2s infinite'
            }}/>
          </motion.div>
        </Box>

        {/* Achievement Highlights Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          style={{
            padding: '6rem 0',
            background: 'linear-gradient(180deg, rgba(255, 0, 255, 0.08) 0%, rgba(0, 0, 0, 0.5) 100%)',
            borderTop: '2px solid rgba(255, 0, 255, 0.4)',
            borderBottom: '2px solid rgba(255, 0, 255, 0.4)',
            boxShadow: 'inset 0 0 100px rgba(255, 0, 255, 0.1)'
          }}
        >
          <Container maxWidth="lg">
            <Typography style={{
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: '1.2rem',
              color: '#ff00ff',
              marginBottom: '3rem',
              textAlign: 'center',
              textShadow: '0 0 20px rgba(255, 0, 255, 0.6)',
              letterSpacing: '0.1em'
            }}>
              [FEATURED_ACHIEVEMENTS]
            </Typography>
            <Box style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {[
                {
                  title: 'ONE_PERSON_ARMY',
                  description: 'Built services from 0 to million users. Experienced in Infra, DevOps, Backend, Frontend, entire stack.',
                  status: 'MACHINE',
                  color: '#00ffff'
                },
                {
                  title: 'TEAM_LEADERSHIP',
                  description: 'Mentorship of junior developers, enforcing coding standards, created reusable components across companies.',
                  status: 'MULTIPLIER',
                  color: '#ff00ff'
                },
                {
                  title: 'PERFORMANCE_BOOST',
                  description: 'Implemented caching strategies, query optimizations, partitions, in order to scale optimally without prematurely adding new servers.',
                  status: 'ENHANCED',
                  color: '#00ff00'
                }
              ].map((achievement, index) => (
                <motion.div
                  key={achievement.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(10, 0, 20, 0.8) 100%)',
                    border: `1px solid ${achievement.color}40`,
                    borderRadius: '8px',
                    padding: '1.5rem',
                    transition: 'all 0.3s ease',
                    cursor: 'default'
                  }}
                  whileHover={{
                    borderColor: achievement.color,
                    boxShadow: `0 4px 15px ${achievement.color}30`
                  }}
                >
                  <Box style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <Typography style={{
                      fontFamily: '"Share Tech Mono", monospace',
                      fontSize: '0.9rem',
                      color: achievement.color,
                      fontWeight: 'bold'
                    }}>
                      {achievement.title}
                    </Typography>
                    <Typography style={{
                      fontFamily: '"Share Tech Mono", monospace',
                      fontSize: '0.7rem',
                      color: achievement.color,
                      background: `${achievement.color}20`,
                      padding: '0.2rem 0.5rem',
                      borderRadius: '3px'
                    }}>
                      {achievement.status}
                    </Typography>
                  </Box>
                  <Typography style={{
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    fontSize: '0.9rem',
                    color: '#ccc',
                    lineHeight: 1.4
                  }}>
                    {achievement.description}
                  </Typography>
                </motion.div>
              ))}
            </Box>
          </Container>
        </motion.section>

        {/* Cyberpunk About Section */}
        <motion.section
          style={{
            ...cyberpunkStyles.aboutSection,
            padding: '6rem 0 4rem 0',
            background: 'linear-gradient(180deg, rgba(0, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0.7) 100%)',
            borderTop: '2px solid rgba(0, 255, 255, 0.4)',
            borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
            boxShadow: 'inset 0 0 100px rgba(0, 255, 255, 0.08)'
          }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
          viewport={{ once: true }}
        >
          <Container maxWidth="lg" style={{ position: 'relative', zIndex: 2 }}>
            {/* Wayfinding Street Sign */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 1 }}
              style={{
                textAlign: 'center',
                marginBottom: '3rem'
              }}
            >
              <Typography style={{
                fontFamily: '"Orbitron", monospace',
                fontSize: '2.5rem',
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #00ffff 0%, #ff00ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 40px rgba(0, 255, 255, 0.3)',
                letterSpacing: '0.2em',
                marginBottom: '1rem'
              }}>
                SELECT YOUR DESTINATION
              </Typography>

              {/* Directional Arrow */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  fontSize: '2rem',
                  color: '#00ffff',
                  marginTop: '1rem',
                  filter: 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.6))'
                }}
              >
                ↓
              </motion.div>
            </motion.div>

            {/* Scroll Trigger for HUD Mode */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              onViewportEnter={() => {
                if (!showResume && !showHRFAQ && !inHUDMode) {
                  console.log('Entering HUD Mode')
                  setInHUDMode(true)
                  setShowHUD(true)
                }
              }}
              onViewportLeave={() => {
                if (inHUDMode && !showResume && !showHRFAQ) {
                  console.log('Leaving HUD Mode')
                  setInHUDMode(false)
                  setShowHUD(false)
                }
              }}
              viewport={{ once: false, margin: '-20%' }}
              style={{
                marginTop: '4rem',
                minHeight: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* Placeholder - actual panels shown in HUD overlay */}
              <Box style={{
                textAlign: 'center',
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: '1.2rem',
                color: '#666',
                letterSpacing: '0.2em'
              }}>
                SCROLL TO ACTIVATE HUD
              </Box>
            </motion.div>
          </Container>
        </motion.section>
      </Box>

      <AnimatePresence>
        {inHUDMode && !showResume && !showHRFAQ && (
          <HUDOverlay
            selectedDestination={selectedDestination}
            onSelectDestination={setSelectedDestination}
            onActivateResume={handleActivateResume}
            onActivateHRFAQ={handleActivateHRFAQ}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showResume && (
          <ContentModal onClose={handleGoBack} color="#00ffff" additionalButtons={<DownloadResumeButton/>}>
            <CyberpunkResumeScrolling/>
          </ContentModal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHRFAQ && (
          <ContentModal onClose={handleGoBack} color="#ff00ff">
            <CyberpunkHRFAQ/>
          </ContentModal>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default CyberLandingPage
